import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Smartphone, 
  Tablet, 
  Monitor,
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  RefreshCw
} from 'lucide-react';

interface DeviceTest {
  device: string;
  viewport: { width: number; height: number };
  status: 'testing' | 'passed' | 'failed' | 'pending';
  issues: string[];
  score: number;
}

interface ResponsiveTest {
  component: string;
  tests: DeviceTest[];
  priority: 'high' | 'medium' | 'low';
}

export default function MobileTesting() {
  const [tests, setTests] = useState<ResponsiveTest[]>([
    {
      component: 'Landing Page',
      priority: 'high',
      tests: [
        { device: 'iPhone SE', viewport: { width: 375, height: 667 }, status: 'pending', issues: [], score: 0 },
        { device: 'iPhone 12', viewport: { width: 390, height: 844 }, status: 'pending', issues: [], score: 0 },
        { device: 'iPad', viewport: { width: 768, height: 1024 }, status: 'pending', issues: [], score: 0 },
        { device: 'Desktop', viewport: { width: 1920, height: 1080 }, status: 'pending', issues: [], score: 0 }
      ]
    },
    {
      component: 'Browse Listings',
      priority: 'high',
      tests: [
        { device: 'iPhone SE', viewport: { width: 375, height: 667 }, status: 'pending', issues: [], score: 0 },
        { device: 'iPhone 12', viewport: { width: 390, height: 844 }, status: 'pending', issues: [], score: 0 },
        { device: 'iPad', viewport: { width: 768, height: 1024 }, status: 'pending', issues: [], score: 0 },
        { device: 'Desktop', viewport: { width: 1920, height: 1080 }, status: 'pending', issues: [], score: 0 }
      ]
    },
    {
      component: 'Referral Form',
      priority: 'high',
      tests: [
        { device: 'iPhone SE', viewport: { width: 375, height: 667 }, status: 'pending', issues: [], score: 0 },
        { device: 'iPhone 12', viewport: { width: 390, height: 844 }, status: 'pending', issues: [], score: 0 },
        { device: 'iPad', viewport: { width: 768, height: 1024 }, status: 'pending', issues: [], score: 0 },
        { device: 'Desktop', viewport: { width: 1920, height: 1080 }, status: 'pending', issues: [], score: 0 }
      ]
    },
    {
      component: 'Provider Dashboard',
      priority: 'medium',
      tests: [
        { device: 'iPhone SE', viewport: { width: 375, height: 667 }, status: 'pending', issues: [], score: 0 },
        { device: 'iPhone 12', viewport: { width: 390, height: 844 }, status: 'pending', issues: [], score: 0 },
        { device: 'iPad', viewport: { width: 768, height: 1024 }, status: 'pending', issues: [], score: 0 },
        { device: 'Desktop', viewport: { width: 1920, height: 1080 }, status: 'pending', issues: [], score: 0 }
      ]
    }
  ]);

  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState(0);

  const runResponsiveTest = async (component: string, device: DeviceTest): Promise<DeviceTest> => {
    // Simulate responsive testing
    await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 2000));
    
    const issues: string[] = [];
    let score = 100;

    // Simulate common responsive issues
    if (device.viewport.width <= 375) {
      if (Math.random() > 0.8) {
        issues.push('Navigation menu overlaps content');
        score -= 20;
      }
      if (Math.random() > 0.7) {
        issues.push('Form inputs too small for touch');
        score -= 15;
      }
      if (Math.random() > 0.9) {
        issues.push('Text too small to read');
        score -= 25;
      }
    }

    if (device.viewport.width <= 768 && device.viewport.width > 375) {
      if (Math.random() > 0.85) {
        issues.push('Cards layout breaks on tablet');
        score -= 15;
      }
      if (Math.random() > 0.8) {
        issues.push('Modal dialogs too wide');
        score -= 10;
      }
    }

    // Check for common issues based on component
    if (component === 'Referral Form') {
      if (device.viewport.width <= 375 && Math.random() > 0.7) {
        issues.push('File upload button not accessible');
        score -= 20;
      }
    }

    if (component === 'Browse Listings') {
      if (device.viewport.width <= 375 && Math.random() > 0.6) {
        issues.push('Filter sidebar overlaps results');
        score -= 25;
      }
    }

    return {
      ...device,
      status: issues.length === 0 ? 'passed' : 'failed',
      issues,
      score: Math.max(score, 0)
    };
  };

  const runAllTests = async () => {
    setIsRunning(true);
    setProgress(0);

    const totalTests = tests.reduce((acc, test) => acc + test.tests.length, 0);
    let completedTests = 0;

    const updatedTests = [...tests];

    for (let i = 0; i < updatedTests.length; i++) {
      const componentTest = updatedTests[i];
      
      for (let j = 0; j < componentTest.tests.length; j++) {
        const deviceTest = componentTest.tests[j];
        
        // Update status to testing
        updatedTests[i].tests[j] = { ...deviceTest, status: 'testing' };
        setTests([...updatedTests]);

        // Run the test
        const result = await runResponsiveTest(componentTest.component, deviceTest);
        
        // Update with results
        updatedTests[i].tests[j] = result;
        setTests([...updatedTests]);
        
        completedTests++;
        setProgress((completedTests / totalTests) * 100);
      }
    }

    setIsRunning(false);
  };

  const resetTests = () => {
    setTests(prev => prev.map(test => ({
      ...test,
      tests: test.tests.map(deviceTest => ({
        ...deviceTest,
        status: 'pending',
        issues: [],
        score: 0
      }))
    })));
    setProgress(0);
  };

  const getDeviceIcon = (device: string) => {
    if (device.includes('iPhone')) return <Smartphone className="h-4 w-4" />;
    if (device.includes('iPad')) return <Tablet className="h-4 w-4" />;
    return <Monitor className="h-4 w-4" />;
  };

  const getStatusIcon = (status: DeviceTest['status']) => {
    switch (status) {
      case 'testing': return <RefreshCw className="h-4 w-4 text-blue-500 animate-spin" />;
      case 'passed': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'failed': return <XCircle className="h-4 w-4 text-destructive" />;
      default: return <div className="h-4 w-4" />;
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-500';
    if (score >= 70) return 'text-yellow-500';
    return 'text-destructive';
  };

  const totalTests = tests.reduce((acc, test) => acc + test.tests.length, 0);
  const passedTests = tests.reduce((acc, test) => 
    acc + test.tests.filter(t => t.status === 'passed').length, 0
  );
  const failedTests = tests.reduce((acc, test) => 
    acc + test.tests.filter(t => t.status === 'failed').length, 0
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Mobile Responsiveness Testing</h2>
        <div className="flex gap-2">
          <Button onClick={resetTests} variant="outline" disabled={isRunning}>
            Reset
          </Button>
          <Button onClick={runAllTests} disabled={isRunning}>
            <RefreshCw className="h-4 w-4 mr-2" />
            {isRunning ? 'Testing...' : 'Run All Tests'}
          </Button>
        </div>
      </div>

      {/* Test Progress */}
      {isRunning && (
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Testing Progress</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <Progress value={progress} />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Test Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Tests</CardTitle>
            <Monitor className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalTests}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Passed</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-500">{passedTests}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Failed</CardTitle>
            <XCircle className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">{failedTests}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {totalTests > 0 ? Math.round((passedTests / totalTests) * 100) : 0}%
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Component Tests */}
      {tests.map((componentTest) => (
        <Card key={componentTest.component}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>{componentTest.component}</CardTitle>
              <Badge variant={componentTest.priority === 'high' ? 'destructive' : 'secondary'}>
                {componentTest.priority} priority
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {componentTest.tests.map((deviceTest) => (
                <div key={deviceTest.device} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      {getDeviceIcon(deviceTest.device)}
                      <span className="font-medium text-sm">{deviceTest.device}</span>
                    </div>
                    {getStatusIcon(deviceTest.status)}
                  </div>
                  
                  <div className="text-xs text-muted-foreground mb-2">
                    {deviceTest.viewport.width}x{deviceTest.viewport.height}
                  </div>
                  
                  {deviceTest.score > 0 && (
                    <div className={`text-lg font-bold mb-2 ${getScoreColor(deviceTest.score)}`}>
                      {deviceTest.score}/100
                    </div>
                  )}
                  
                  {deviceTest.issues.length > 0 && (
                    <div className="space-y-1">
                      <div className="text-xs font-medium text-destructive">Issues:</div>
                      {deviceTest.issues.map((issue, index) => (
                        <div key={index} className="text-xs text-destructive">
                          • {issue}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}